/**
 * Synchronous, localStorage-shaped view over the user's server-stored
 * preferences.
 *
 * Every consumer already does `getItem` -> JSON.parse and JSON.stringify ->
 * `setItem`, so values are kept as raw JSON strings and no call site has to
 * change its parsing or become async.
 *
 * Writes are DROPPED unless a load has succeeded. A failed read must never let
 * the client overwrite good server state with defaults — that is the single
 * most important rule here.
 */

export type PrefsStatus = 'idle' | 'loading' | 'ready' | 'failed';

export const FLUSH_DELAY_MS = 500;

type Flusher = (patch: Record<string, string>) => void;

let values: Record<string, string> = {};
let pending: Record<string, string> = {};
let status: PrefsStatus = 'idle';
let flusher: Flusher | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    flushNow();
  }, FLUSH_DELAY_MS);
}

function flushNow(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (status !== 'ready') return;
  const patch = pending;
  pending = {};
  if (!flusher || Object.keys(patch).length === 0) return;
  flusher(patch);
}

export const prefsStore = {
  getItem(key: string): string | null {
    return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null;
  },

  setItem(key: string, value: string): void {
    if (status !== 'ready') return;
    values[key] = value;
    pending[key] = value;
    scheduleFlush();
  },

  removeItem(key: string): void {
    if (status !== 'ready') return;
    delete values[key];
    // An empty string is the erase signal: the merge endpoint cannot express
    // "delete this key", and every consumer treats "" as absent because
    // JSON.parse('') throws and their catch falls back to the default.
    pending[key] = '';
    scheduleFlush();
  },

  hydrate(data: Record<string, string>): void {
    values = { ...data };
  },

  reset(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    values = {};
    pending = {};
    status = 'idle';
  },

  setStatus(s: PrefsStatus): void {
    status = s;
  },

  getStatus(): PrefsStatus {
    return status;
  },

  setFlusher(fn: Flusher | null): void {
    flusher = fn;
  },

  flushNow,
};
